import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ServiceBooking } from '@turizi/shared';
import { RESERVATIONS_PATH } from '@/lib/navigation';
import { BookingGroupSize } from '@/components/snippets';
import ProviderSelector from '@/components/service-booking/provider-selector';
import CoordinatorSelector from '@/components/service-booking/coordinator-selector';
import BookingServiceOrders from '@/components/service-booking/booking-service-orders';
import ShowSelfAttrs from '@/components/show-self-attrs';
import { ServiceTitleStatic } from './services/service-title';
import { TagIcon } from '@/lib/tag-utils';
import { IconBuildingStore } from '@tabler/icons-react';

interface SmallBookingsListProps {
    bookings: ServiceBooking[];
    exclude?: string[];
}

export default function SmallBookingsList({ bookings = [], exclude = ['flight_number'] }: SmallBookingsListProps) {
    const { t } = useTranslation();

    if (!bookings || bookings.length === 0) {
        return (
            <span className="text-xs text-gray-400 font-medium italic">
                {t('no_bookings_found')}
            </span>
        );
    }

    return (
        <div className="flex flex-col gap-2 w-full grow">
            {bookings.map((booking) => {
                const reservation = booking.reservation;
                const clientName = reservation?.client_business?.name;
                return (
                    <div
                        key={booking.id}
                        className="flex flex-col gap-1.5 p-2 bg-slate-50/90 rounded-lg border border-slate-200/80 text-xs w-full"
                    >
                        {/* Row 1: Passengers (Pax) & Selectors & Booking ID */}
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <BookingGroupSize booking={booking} />
                            </div>

                            <div className="flex flex-wrap items-center gap-1.5 ml-auto">
                                <ProviderSelector booking={booking} size="xs" />
                                <CoordinatorSelector booking={booking} size="xs" />
                                <Link
                                    to={reservation ? `${RESERVATIONS_PATH}/${reservation.id}` : '#'}
                                    className="font-extrabold text-blue-600 hover:underline px-1.5 py-0.5 bg-blue-50 border border-blue-100 rounded-md text-[11px]"
                                >
                                    #{booking.id}
                                </Link>
                                <BookingServiceOrders booking={booking} />
                            </div>
                        </div>

                        {/* Row 2: Service Title & Client */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5 border-t border-slate-200/50 text-xs">
                            <div className="flex items-center gap-1.5 font-semibold text-slate-800">
                                <TagIcon name={booking.service_data?.category} noBackground hideText size="xs" />
                                <span className="whitespace-normal break-words">{ServiceTitleStatic(booking.service_data)}</span>
                            </div>
                            {clientName && (
                                <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-slate-200 rounded text-xxs font-medium text-slate-700 whitespace-normal break-words">
                                    <IconBuildingStore size={12} className="text-slate-400" />
                                    <span>{clientName}</span>
                                </div>
                            )}
                        </div>

                        {/* Self attributes */}
                        {booking.self_attrs && booking.self_attrs.length > 0 && (
                            <div className="pt-0.5">
                                <ShowSelfAttrs
                                    exclude={exclude}
                                    showkey={false}
                                    attrs={booking.self_attrs}
                                    size="xs"
                                />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
